import { useLocation, useRouter } from '@tanstack/react-router';
import { getStrictCurrentEnterpriseId } from '@mews-commander/core';
import { GUID_FORMAT_REGEX } from '@mews-commander/modules/src/constants';
import { Redirect } from '@mews-commander/ui';

/**
 * Tries to match the route if no enterprise ID is provided in URL.
 */
export const EnterpriseIdFallbackComponent = ({ notFoundComponent }: { notFoundComponent: React.ReactElement }) => {
    const location = useLocation();
    const router = useRouter();
    const currentEnterpriseId = getStrictCurrentEnterpriseId();

    // redirect to login if there's no enterprise ID in the session
    if (currentEnterpriseId === undefined) {
        return <Redirect to="/Home/SignIn" />;
    }

    // redirect to a path prefixed with currentEnterpriseId, preserving passed params and search
    const path = location.pathname.replace(new RegExp(`^(${router.basepath})+`, 'ig'), '');
    const matches = router.matchRoutes(
        router.buildLocation({
            to: `/$currentEnterpriseId${path}`,
            params: (params) => ({ ...params, currentEnterpriseId }),
            search: location.search,
            hash: location.hash,
        })
    );
    if (matches.length > 1) {
        return <Redirect to={matches.at(-1)?.pathname} search={matches.at(-1)?.search} hash={location.hash} />;
    }

    // redirect to a path prefixed with currentEnterpriseId if the path starts with the ID
    const pathStartingWithId = new RegExp(`(${GUID_FORMAT_REGEX.source}/.*)`).exec(path);
    if (pathStartingWithId?.[0]) {
        const matchesWithId = router.matchRoutes(
            router.buildLocation({
                to: router.basepath + '/' + pathStartingWithId[0],
                params: true,
                search: location.search,
                hash: location.hash,
            })
        );
        if (matchesWithId.length > 1) {
            return (
                <Redirect
                    to={matchesWithId.at(-1)?.pathname}
                    search={matchesWithId.at(-1)?.search}
                    hash={location.hash}
                />
            );
        }
    }

    return notFoundComponent;
};
